import { type FC, useEffect } from "react";

import { Dialog, TriggerButton, TriggerButtonIntent, TriggerButtonSize, useDialogWithPosition } from "widgets";

import CategoricalConditionValuesFilter, {
  type IConditionValuesFilterProps,
} from "./CategoricalConditionValuesFilter.js";
import { FilterChart, type IFilterChartData } from "./FilterChart.js";
import NumericalConditionValuesFilter from "./NumericalConditionValuesFilter.js";
import type { ColumnAnnotation } from "@/graphql.ts";
import type { IColumnAnnotationFilter, IColumnAnnotationFilterMap } from "@/redux/modules/dataset-viewer.ts";
import { ANNOTATION_TYPE } from "@/settings/settings.js";

export interface IFilterPanelItemProps {
  condition: ColumnAnnotation;
  filteredConditions: IColumnAnnotationFilterMap;
  getHistogramData: (condition: ColumnAnnotation) => IFilterChartData;

  filteredConditionsChanged: (conditionField: string, conditionFilter: IColumnAnnotationFilter) => void;
}

/**
 * Component representing a condition/annotation in the context of the Analytical Data Viewer filter panel.
 *
 * created on 4/27/2017
 */
const FilterPanelItem: FC<IFilterPanelItemProps> = (props) => {
  const { triggerRef, dialogRef, triggerProps, dialogProps, arrowProps, state, updatePosition } = useDialogWithPosition<
    HTMLButtonElement,
    HTMLDivElement
  >({
    placement: "left",
  });

  const filterData: IFilterChartData = props.getHistogramData(props.condition);

  useEffect(() => {
    // The dialog height is dynamic depending on if the user wants to see all content collapsed or expanded.
    if (state.isOpen) updatePosition();
  }, [filterData]);

  if (!filterData) {
    return null;
  }

  const isFilterActive = (): boolean => {
    const conditionFilter: IColumnAnnotationFilter = props.filteredConditions[props.condition.field];
    return (
      conditionFilter &&
      ((conditionFilter.categoricalValues && conditionFilter.categoricalValues.length > 0) ||
        !!conditionFilter.minNumericalValue ||
        !!conditionFilter.maxNumericalValue)
    );
  };

  const conditionValuesFilterProps: IConditionValuesFilterProps = {
    condition: props.condition,
    filterData: filterData,
    filteredConditions: props.filteredConditions,
    filteredConditionsChanged: props.filteredConditionsChanged,
  };

  return (
    <div className="chart-filters-item" id={"chart-filters-item" + props.condition.name}>
      <TriggerButton
        {...triggerProps}
        icon={"filter_alt"}
        label={`Filter based on ${props.condition.name}`}
        intent={isFilterActive() ? TriggerButtonIntent.Active : TriggerButtonIntent.Outlined}
        size={TriggerButtonSize.Small}
        ref={triggerRef}
      />
      <span className="chart-filters-item-text" title={props.condition.name}>
        {props.condition.name}
      </span>
      <FilterChart width={120} height={40} chartData={filterData} />
      {state.isOpen && (
        <Dialog
          closeFn={state.close}
          id={"valuesFilterPopover"}
          className="chart-filters-popup"
          domProps={dialogProps}
          arrowProps={arrowProps}
          ref={dialogRef}
        >
          {props.condition.type === ANNOTATION_TYPE.NUMERICAL ? (
            <NumericalConditionValuesFilter {...conditionValuesFilterProps} />
          ) : (
            <CategoricalConditionValuesFilter {...conditionValuesFilterProps} />
          )}
        </Dialog>
      )}
    </div>
  );
};

export default FilterPanelItem;
